import {
  Checkbox,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useField } from 'formik';
import { important } from '../../../../utils/global/helper';
import InputWrapper from '../InputWrapper';
import { getInputBorderStyle } from '../../../../helpers/styles';

interface MultiSelectProps {
  name: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  options: {
    value: string;
    label: string;
  }[];
  tooltip?: string;
  allText?: string;
  autoWidth?: boolean;
  required?: boolean;
  showSelectAll?: boolean;
}

function MultiSelect({
  name,
  label,
  placeholder,
  disabled,
  options,
  tooltip,
  allText,
  autoWidth = true,
  required,
  showSelectAll = true,
}: MultiSelectProps) {
  const [{ value }, { error, touched }, { setValue, setTouched }] = useField(
    name.toString(),
  );
  const isError = !!error && !!touched;

  const selectedList = (value ?? []) as string[];

  const isAllSelected = options.every(option =>
    selectedList.includes(option.value),
  );

  const renderValue = (selected: string[]) => {
    if (!selected.length) {
      return placeholder || `No ${name}`;
    }
    if (isAllSelected) {
      return allText || `All ${name}`;
    }
    const selectedLabels = selected.map(
      id => options.find(option => option.value === id)?.label,
    );
    return selectedLabels.join(', ');
  };

  return (
    <InputWrapper
      label={label}
      name={name}
      tooltip={tooltip}
      required={required}>
      <Select
        multiple
        renderValue={selected => renderValue(selected as unknown as string[])}
        sx={theme => ({
          height: important('44px'),
          fontSize: 'body2.fontSize',
          '& .MuiOutlinedInput-notchedOutline': getInputBorderStyle({
            theme,
            hideBorder: false,
            isError,
          }),
        })}
        value={selectedList as unknown as string}
        onChange={async (event: SelectChangeEvent) => {
          const { value: newValue } = event.target;
          const newList = newValue as unknown as string[];
          if (newList.includes('all') && !isAllSelected) {
            setValue(options.map(option => option.value));
            return;
          }
          if (newList.includes('all') && isAllSelected) {
            setValue([]);
            return;
          }
          setValue(newList);
        }}
        input={<OutlinedInput />}
        autoWidth={autoWidth}
        displayEmpty
        label={placeholder}
        disabled={disabled}
        onBlur={() => {
          setTouched(true);
        }}>
        {showSelectAll && (
          <MenuItem value="all" sx={{ gap: 1 }}>
            <Checkbox sx={{ padding: 0.5 }} checked={isAllSelected} />
            <ListItemText
              primaryTypographyProps={{ fontSize: 'body2.fontSize' }}
              primary={allText || `All ${name}`}
            />
          </MenuItem>
        )}
        {options.map(option => (
          <MenuItem key={option.value} value={option.value} sx={{ gap: 1 }}>
            <Checkbox
              checked={selectedList.includes(option.value)}
              sx={{
                padding: 0.5,
              }}
            />
            <ListItemText
              primaryTypographyProps={{ fontSize: 'body2.fontSize' }}
              primary={option.label}
            />
          </MenuItem>
        ))}
      </Select>
    </InputWrapper>
  );
}

export default MultiSelect;
